import { createAsyncThunk } from '@reduxjs/toolkit';

import { APIBaseResponse } from '..';
import { handleListFetch } from '../apiHelpers';
import { APISuiteGet } from '../ApplicationSuites/ApplicationSuites.api';
import { APITenantGetResponse } from '../Tenants/TenantApiTypeDefs';
import { APITenantGet } from '../Tenants/Tenants.api';

import {
    APIClaimDelete,
    APIClaimGet,
    APIClaimGetResponse,
    APIClaimPost,
    APIClaimPostParams,
    APIClaimPostResponse,
    APIClaimPut,
    APIClaimPutParams,
    APIClaimsGetList,
    Claim
} from './Claims.api';

type FetchClaimDetailsParams = {
    id: string;
    tenantId: string;
    applicationSuiteId: string;
}

export type FetchClaimDetailsResponse = {
    applicationSuiteId: string,
    applicationSuiteName: string,
    tenantId: string,
    tenantName: string,
    tenantRequiresLicense: boolean,
    id: string,
    name: string,
    claimDataTypeId: string,
    claimDataTypeName: string
}

export const fetchClaims = createAsyncThunk('claims/fetchClaims', async (item: { tenantId: string, signal?: AbortSignal }) => {
    const response = await APIClaimsGetList(item);
    return response.data;
});

export type PostClaimThunkResponse = {
    item: APIClaimPostParams,
    response: APIClaimPostResponse,
    rows: Claim[]
}

export const postClaim = createAsyncThunk('claims/postClaim', async (item: APIClaimPostParams) => {
    const response = await APIClaimPost(item);
    const rows = await handleListFetch(APIClaimsGetList({ tenantId: item.tenantId }));
    return {
        item,
        response,
        rows,
    };
});

export const deleteClaim = createAsyncThunk('claims/deleteClaim', async (item: { tenantId: string, id: string }) => {
    const response = await APIClaimDelete(item);
    const rows = await handleListFetch(APIClaimsGetList({ tenantId: item.tenantId }));
    return {
        response,
        rows
    };
});

export const fetchClaimDetails = createAsyncThunk('claimDetails/fetchDetails', async (fetchParams: FetchClaimDetailsParams): Promise<FetchClaimDetailsResponse> => {
    const requests = [
        APISuiteGet({ id: fetchParams.applicationSuiteId }),
        APITenantGet({ id: fetchParams.tenantId }),
        APIClaimGet({ tenantId: fetchParams.tenantId, id: fetchParams.id })
    ];
    const responses = await Promise.all(requests);
    const apiClaimResponse = responses[2] as APIClaimGetResponse;
    const tenantResponse = responses[1] as APITenantGetResponse;
    return {
        applicationSuiteId: responses[0].data.id,
        applicationSuiteName: responses[0].data.name,
        tenantId: tenantResponse.data.id,
        tenantName: tenantResponse.data.name,
        tenantRequiresLicense: tenantResponse.data.isLicenseRequired,
        id: apiClaimResponse.data.id,
        name: apiClaimResponse.data.name,
        claimDataTypeId: apiClaimResponse.data.claimDataTypeId,
        claimDataTypeName: apiClaimResponse.data.claimDataTypeName
    };
});

export type PutClaimThunkResponse = {
    item: APIClaimPutParams,
    response: APIBaseResponse,
    rows: Claim[]
}

export const putClaim = createAsyncThunk('claims/putClaim', async (item: APIClaimPutParams) => {
    const response = await APIClaimPut(item);
    const rows = await handleListFetch(APIClaimsGetList({ tenantId: item.tenantId }));
    return {
        item,
        response,
        rows,
    };
});
